import React from 'react'
import { Flow, Term, Tier, PricesList } from '../types'
import { entryFor, buildProCard, buildPremiumCard } from '../lib/planViewModel'
import PlanCard from './PlanCard'

interface FlowPlansProps {
  flow: Flow
  term: Term
  annualPrices: PricesList | null
  sprintPrices: PricesList | null
  // Premium is only offered to accounts that have held a Premium license before.
  showPremium: boolean
  // Students/children already on the account; per-seat plans quote a total for them.
  seatsUsed: number
  onSelect: (flow: Flow, tier: Tier) => void
  onTermChange: (term: Term) => void
}

/** The Pro card for one flow, plus its legacy Premium card when that plan applies. */
function FlowPlans({
  flow,
  term,
  annualPrices,
  sprintPrices,
  showPremium,
  seatsUsed,
  onSelect,
  onTermChange,
}: FlowPlansProps) {
  const proEntry = entryFor(flow, 'pro', term, annualPrices, sprintPrices)
  if (!proEntry) return null

  const pro = buildProCard(
    flow,
    proEntry,
    term,
    annualPrices,
    sprintPrices,
    seatsUsed
  )
  const premium = showPremium
    ? buildPremiumCard(
        flow,
        entryFor(flow, 'premium', term, annualPrices, sprintPrices)
      )
    : null

  return (
    <>
      <PlanCard
        planName={pro.planName}
        priceDisplay={pro.priceDisplay}
        originalPriceDisplay={pro.originalPriceDisplay}
        discountLabel={pro.discountLabel}
        priceUnit={pro.priceUnit}
        subtitle={pro.subtitle}
        note={pro.note}
        features={pro.features}
        ctaLabel='Continue'
        onCheckout={() => onSelect(flow, 'pro')}
        recommended={pro.recommended}
        showBadge={pro.showBadge}
        termOptions={pro.termOptions}
        term={term}
        onTermChange={onTermChange}
      />

      {premium && (
        <PlanCard
          planName={premium.planName}
          priceDisplay={premium.priceDisplay}
          priceUnit={premium.priceUnit}
          chip={premium.chip}
          features={premium.features}
          ctaLabel='I Prefer Premium Plan'
          onCheckout={() => onSelect(flow, 'premium')}
          recommended={false}
        />
      )}
    </>
  )
}

export default FlowPlans
